import { useEffect, useState } from "react";
import api from "../lib/api";
import { Plane, Clock, Hotel, MapPin } from "lucide-react";

const KIND_TONE = {
  program: "bg-emerald-50 border-emerald-200 text-emerald-800",
  weekend: "bg-sky-50 border-sky-200 text-sky-800",
};

/**
 * Compact strip showing "arrivals today + tomorrow" scoped to the caller's
 * office so GEHM/PCWM/BDOM sees who is landing without leaving My Day.
 */
export default function ArrivalsTodayStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/my-office/arrivals-today")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err || !data) return null;
  if (!data.count) {
    return (
      <div className="aii-card p-4 border-l-4 border-[color:var(--aii-border)]" data-testid="arrivals-today-empty">
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Arriving today & tomorrow</div>
        <div className="text-sm text-[color:var(--aii-text-2)] mt-1">
          🛬 Nobody landing in <b>{data.location}</b> in the next 24 hours. Deep breath, then back to the checklist.
        </div>
      </div>
    );
  }

  return (
    <div className="aii-card p-4 border-l-4 border-brand" data-testid="arrivals-today-strip">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Arriving today & tomorrow</div>
          <div className="font-display font-semibold text-base flex items-center gap-2">
            <Plane className="text-brand" size={16}/>
            {data.count} participant{data.count === 1 ? "" : "s"} landing in <span className="text-brand">{data.location}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {data.arrivals.map((a) => (
          <div key={`${a.kind}-${a.id}`} data-testid={`arrival-strip-${a.id}`}
            className={`border rounded p-2.5 text-sm ${KIND_TONE[a.kind] || "bg-white border-[color:var(--aii-border)]"}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold truncate">{a.customer || "Unknown"}</div>
                <div className="text-xs opacity-80 truncate">{a.programme || (a.kind === "weekend" ? "Weekend tour" : "Programme")}</div>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-semibold shrink-0">{a.when_label}</span>
            </div>
            <div className="text-[11px] mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 opacity-90">
              {(a.airline || a.flight_number) && (
                <span className="inline-flex items-center gap-1">
                  <Plane size={10}/> {[a.airline, a.flight_number].filter(Boolean).join(" ")}
                </span>
              )}
              {a.flight_time && (
                <span className="inline-flex items-center gap-1"><Clock size={10}/> {a.flight_time}</span>
              )}
              {a.arrival_airport && (
                <span className="inline-flex items-center gap-1"><MapPin size={10}/> {a.arrival_airport}</span>
              )}
              {a.hotel_name && (
                <span className="inline-flex items-center gap-1"><Hotel size={10}/> {a.hotel_name}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
